import { useState } from 'react';

import { CopyableCpfCnpj, CopyableEmail, CopyablePlaca, CopyableTelefone } from '@/ui/copyable';
import { CopyableText } from '@/ui/CopyableText';
import { EmptyValue } from '@/ui/EmptyValue';
import { GlossaryTerm } from '@/ui/GlossaryTerm';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { RelativeTime } from '@/ui/RelativeTime';
import { TipoPessoaBadge } from '@/ui/TipoPessoaBadge';
import { UpdatedAt } from '@/ui/UpdatedAt';

import { AmostraSuspensa } from '../AmostraSuspensa';
import { ComponentShowcase } from '../ComponentShowcase';
import { MEDIDAS } from '../medidas';
import { FORMATOS_DE_EXEMPLO } from '../exemplos';

const MINUTO = 60_000;

/** Selos e valores formatados: o que aparece dentro das células e dos cartões. */
export function AmostrasDeValores() {
  // Datas calculadas a partir de agora, para a amostra não envelhecer.
  const [agora] = useState(() => Date.now());
  const [atualizadoEm, definirAtualizadoEm] = useState(() => agora - 3 * MINUTO);

  return (
    <>
      <ComponentShowcase
        id="tipo-pessoa-badge"
        title="TipoPessoaBadge"
        description="Natureza do credenciado: pessoa física, pessoa jurídica ou cooperativa (que é PJ para a lei, mas ganha ícone próprio porque o rateio entre cooperados muda o que ela vê). Sem cor de situação, de propósito: natureza não é situação."
        code={`<TipoPessoaBadge tipo="PF" />
<TipoPessoaBadge tipo="COOPERATIVA" compacto />`}
        propsTable={[
          [
            'tipo',
            "'PF' | 'PJ' | 'COOPERATIVA' | string | null",
            '-',
            'Tipo do credenciado; desconhecido ou vazio mostra “—”',
          ],
          ['compacto', 'boolean', 'false', 'Sigla curta (PF, PJ, Coop.) para coluna estreita'],
          ['className', 'string', '-', 'Classes do selo'],
        ]}
      >
        <TipoPessoaBadge tipo="PF" />
        <TipoPessoaBadge tipo="PJ" />
        <TipoPessoaBadge tipo="COOPERATIVA" />
        <TipoPessoaBadge tipo="PF" compacto />
        <TipoPessoaBadge tipo="COOPERATIVA" compacto />
      </ComponentShowcase>

      <ComponentShowcase
        id="copyable-text"
        title="CopyableText"
        description="Mostra um valor e o copia pelo botão com rótulo próprio (não ao passar o mouse). O retorno “Copiado” é anunciado ao leitor de tela. O clique não sobe para a linha: copiar um protocolo numa listagem não abre o detalhe."
        code={`<CopyableText value="SEI-0000.0000/0000000-0" label="Copiar protocolo" />`}
        propsTable={[
          [
            'value',
            'string | null',
            '-',
            'O que vai para a área de transferência; vazio = sem botão',
          ],
          ['display', 'string', 'value', 'O que aparece, se diferente (ex.: mascarado)'],
          ['label', 'string', '"Copiar {value}"', 'Rótulo do botão para leitor de tela'],
          ['maxWidth', 'string', '-', 'Largura máxima, com corte (ex.: max-w-[200px])'],
          ['className', 'string', '-', 'Classes do contêiner'],
        ]}
      >
        <CopyableText value={FORMATOS_DE_EXEMPLO.protocolo} label="Copiar protocolo" />
      </ComponentShowcase>

      <ComponentShowcase
        id="copyable-variantes"
        title="CopyableCpfCnpj, CopyableTelefone, CopyablePlaca e CopyableEmail"
        description="As variantes que as listagens usam. Uma regra só: o que aparece é mascarado, o que vai para a área de transferência é cru (quem copia um CPF vai colar em outro sistema). E-mail não tem máscara, tem corte. Os valores abaixo são só zeros: mostram o formato, não são de ninguém."
        code={`<CopyableCpfCnpj value={credenciado.documento} />
<CopyableTelefone value={contato.telefone} />
<CopyablePlaca value={veiculo.placa} />
<CopyableEmail value={contato.email} />`}
        propsTable={[
          ['value', 'string | null', '-', 'Valor cru; o componente mascara o que mostra'],
          ['className', 'string', '-', 'Classes do contêiner'],
        ]}
      >
        <div className="grid w-full gap-2 sm:grid-cols-2">
          <CopyableCpfCnpj value={FORMATOS_DE_EXEMPLO.cpf} />
          <CopyableCpfCnpj value={FORMATOS_DE_EXEMPLO.cnpj} />
          <CopyableTelefone value={FORMATOS_DE_EXEMPLO.telefone} />
          <CopyablePlaca value={FORMATOS_DE_EXEMPLO.placa} />
          <CopyableEmail value={FORMATOS_DE_EXEMPLO.email} />
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="money-display"
        title="MoneyDisplay"
        description="Dinheiro em real, para leitura. Aceita o valor do banco (“1234.56”), o digitado (“1.234,56”) ou um número; sem valor, travessão."
        code={`<MoneyDisplay value={medicao.valor} />
<MoneyDisplay value={null} emptyLabel="Não apurado" />`}
        propsTable={[
          ['value', 'string | number | null', '-', 'Valor em reais'],
          ['emptyLabel', 'string', '"—"', 'O que mostrar sem valor'],
          ['className', 'string', '-', 'Classes do texto'],
        ]}
      >
        <MoneyDisplay value="45320.00" className="font-mono" />
        <MoneyDisplay value={-1250.5} className="font-mono" />
        <MoneyDisplay value={null} emptyLabel="Não apurado" />
      </ComponentShowcase>

      <ComponentShowcase
        id="empty-value"
        title="EmptyValue"
        description="Valor ausente, padronizado: travessão (lido como “Sem informação”), “Não informado” ou “Sem sincronização”. Sempre em texto atenuado."
        code={`<EmptyValue />
<EmptyValue variant="informed" />
<EmptyValue variant="sync" />`}
        propsTable={[
          ['label', 'string', '-', 'Texto próprio no lugar do padrão da variante'],
          ['variant', '"dash" | "informed" | "sync"', '"dash"', 'Qual ausência'],
          ['className', 'string', '-', 'Classes do texto'],
        ]}
      >
        <EmptyValue />
        <EmptyValue variant="informed" />
        <EmptyValue variant="sync" />
      </ComponentShowcase>

      <ComponentShowcase
        id="relative-time"
        title="RelativeTime"
        description="Data relativa (“há 5 minutos”) com a data absoluta na dica, ao passar o mouse ou focar."
        code={`<RelativeTime date={registro.updated_at} />`}
        propsTable={[
          ['date', 'string | Date | null', '-', 'A data; nula ou inválida mostra o emptyLabel'],
          ['tooltipFormat', 'string', '"dd/MM/yyyy HH:mm"', 'Formato da data absoluta na dica'],
          ['emptyLabel', 'string', '"—"', 'Texto sem data'],
          ['className', 'string', '-', 'Classes do texto'],
        ]}
      >
        <RelativeTime date={new Date(agora - 5 * MINUTO)} />
        <RelativeTime date={new Date(agora - 3 * 24 * 60 * MINUTO)} />
        <RelativeTime date={null} />
      </ComponentShowcase>

      <ComponentShowcase
        id="updated-at"
        title="UpdatedAt"
        description="“Atualizado há N” do cabeçalho de painel, com o botão de recarregar quando a tela permite. Reescreve o rótulo sozinho a cada 30 segundos."
        code={`<UpdatedAt
  updatedAt={consulta.dataUpdatedAt}
  isFetching={consulta.isFetching}
  onRefresh={() => void consulta.refetch()}
/>`}
        propsTable={[
          ['updatedAt', 'number | null', '-', 'Momento em ms (dataUpdatedAt do React Query)'],
          ['isFetching', 'boolean', 'false', 'Revalidando: gira o ícone e diz “Atualizando…”'],
          ['onRefresh', '() => void', '-', 'Recarregar à mão; sem ele, não há botão'],
          ['className', 'string', '-', 'Classes do contêiner'],
        ]}
      >
        <UpdatedAt updatedAt={atualizadoEm} onRefresh={() => definirAtualizadoEm(Date.now())} />
      </ComponentShowcase>

      <ComponentShowcase
        id="destructive-action-chip"
        title="DestructiveActionChip"
        description="Selo junto do título de um diálogo para avisar que a ação não tem volta. É anunciado ao leitor de tela sem precisar de foco."
        code={`<DialogTitle className="flex items-center gap-2">
  Rescindir termo <DestructiveActionChip />
</DialogTitle>`}
        propsTable={[
          ['label', 'string', '"Ação irreversível"', 'Texto do selo'],
          ['tone', '"destructive" | "warning"', '"destructive"', 'Cor do selo'],
          ['size', '"sm" | "md"', '"sm"', 'Tamanho'],
          ['className', 'string', '-', 'Classes do selo'],
        ]}
      >
        <AmostraSuspensa
          item="destructive-action-chip"
          medida={`destrutivo ${MEDIDAS.seloDestrutivo}; atenção ${MEDIDAS.seloDeAtencao}`}
        />
      </ComponentShowcase>

      <ComponentShowcase
        id="glossary-term"
        title="GlossaryTerm"
        description="Termo do domínio com a definição num balão (clique ou Enter). As definições ficam num catálogo só (GLOSSARIO, em ui/GlossaryTerm.tsx), para a mesma sigla não ganhar duas explicações."
        code={`<GlossaryTerm termo="DAMSP" />
<GlossaryTerm termo="COMPETENCIA">competência</GlossaryTerm>`}
        propsTable={[
          ['termo', 'ChaveDoGlossario', '-', 'Chave do termo em GLOSSARIO'],
          ['children', 'ReactNode', 'a chave', 'Texto exibido'],
          ['esconderIcone', 'boolean', 'false', 'Só o sublinhado, sem o “?”'],
          ['className', 'string', '-', 'Classes do gatilho'],
        ]}
      >
        <p className="text-sm text-foreground">
          Termos como <GlossaryTerm termo="DAMSP" /> e{' '}
          <GlossaryTerm termo="COMPETENCIA">competência</GlossaryTerm> trazem a definição ao lado.
        </p>
      </ComponentShowcase>
    </>
  );
}
